import React, { useState, useEffect } from "react";
import UploadModal from "../modal/UploadModal";
import { ArrowUpTrayIcon, PhotoIcon, PencilIcon, ClipboardIcon, CheckIcon, TrashIcon } from "@heroicons/react/24/outline";
import { assetsApi } from "../../repositories/assets";
import Swal from 'sweetalert2';
import { Skeleton } from '../ui/skeleton';
import { confirmDanger } from '../../utils/swal';
import Button from '../ui/Button';
interface Asset {
  id: number;
  userId: number;
  artistId: string;
  url: string;
  imageTitle: string;
  createdAt: string;
  updatedAt: string;
}

interface AssetsTabProps {
  artistId: string;
}

const AssetsTab: React.FC<AssetsTabProps> = ({ artistId }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState<string>("");
  const [updating, setUpdating] = useState(false);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  useEffect(() => {
    if (artistId) {
      loadAssets();
    }
  }, [artistId]);

  const loadAssets = async () => {
    try {
      setLoading(true);
      const response = await assetsApi.getByArtistId(artistId);
      setAssets(response.data?.data.images || []);
    } catch (error) {
      console.error('Error loading artists:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSuccess = (newAssets: Asset[]) => {
    setAssets(prevAssets => [...prevAssets, ...newAssets]);
  };

  const startEditing = (index: number, asset: Asset) => {

    setEditingId(index);
    setEditTitle(asset.imageTitle);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditTitle("");
  };

  const handleCopyUrl = async (url: string, index: number) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(index);
      
      // Show success message with Swal
      Swal.fire({
        icon: 'success',
        title: 'Copied!',
        text: 'Image link copied!',
        showConfirmButton: false,
        timer: 1500,
        position: 'top-end',
        toast: true
      });
      
      // Reset the copied state after 2 seconds
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to copy URL',
      });
    }
  };

  const handleUpdateTitle = async (artistId: string, index: number, newTitle: string) => {
    setUpdating(true);
    try {

      if (!newTitle.trim()) {
        Swal.fire({
          icon: 'warning',
          title: 'Validation Error',
          text: 'Title cannot be empty.',
        });
        return;
      }
      await assetsApi.update(artistId, index, newTitle);
      setAssets(prev =>
        prev.map((a, i) =>
          i === index ? { ...a, imageTitle: newTitle } : a
        )
      );
      setEditingId(null);
      setEditTitle("");
      Swal.fire({
        icon: 'success',
        title: 'Saved!',
        text: 'Asset name updated.',
        showConfirmButton: false,
        timer: 1500,
        position: 'top-end',
        toast: true,
      });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update asset name. Please try again.',
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteAsset = async (index: number, asset: Asset) => {
    const result = await confirmDanger({
      title: 'Delete asset?',
      text: 'This will permanently remove this asset. This action cannot be undone.',
      confirmButtonText: 'Delete',
    });
    if (!result.isConfirmed) return;

    setDeletingId(index);
    try {
      // The backend identifies the asset by its URL, not by index.
      await assetsApi.delete(artistId, asset.url);
      // Reload from the server so indexes stay in sync with the backend after removal.
      await loadAssets();
      // If the deleted asset was being edited, close the editor.
      if (editingId === index) {
        setEditingId(null);
        setEditTitle("");
      }
      Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        text: 'Asset removed.',
        showConfirmButton: false,
        timer: 1500,
        position: 'top-end',
        toast: true,
      });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to delete asset. Please try again.',
      });
    } finally {
      setDeletingId(null);
    }
  };
  return (
    <div className="p-6 space-y-6">
      {loading ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" aria-busy="true">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="w-full aspect-square rounded-lg" />
              <Skeleton className="w-2/3 h-3" />
            </div>
          ))}
        </div>
      ) : assets.length > 0 ? (
        <>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-black">Artist Assets</h2>
              <p className="text-sm text-neutral-500 mt-1">
                Manage photos and other assets for this artist
              </p>
            </div>
            <Button onClick={() => setIsModalOpen(true)} data-tour="upload-asset-btn">
              <ArrowUpTrayIcon className="w-4 h-4" />
              Upload More Assets
            </Button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">

            
            {assets.map((asset, index) => (
              <div key={index} className="rounded-lg overflow-hidden border border-neutral-200">
                <div className="aspect-square bg-neutral-100 relative group">
                  <img
                    src={asset.url}
                    alt={asset.imageTitle}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 right-2 flex gap-2">
                    <button
                      onClick={() => handleCopyUrl(asset.url, index)}
                      className="p-1.5 bg-white bg-opacity-90 rounded-full hover:bg-opacity-100 transition-all shadow-sm"
                      title="Copy image URL"
                    >
                      {copiedId === index ? (
                        <CheckIcon className="w-4 h-4 text-success-600" />
                      ) : (
                        <ClipboardIcon className="w-4 h-4 text-neutral-600 hover:text-black" />
                      )}
                    </button>
                    <button
                      onClick={() => handleDeleteAsset(index, asset)}
                      disabled={deletingId === index}
                      className="p-1.5 bg-white bg-opacity-90 rounded-full hover:bg-opacity-100 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Delete asset"
                    >
                      {deletingId === index ? (
                        <span className="block w-4 h-4 border-2 border-danger-600 rounded-full animate-spin border-t-transparent" />
                      ) : (
                        <TrashIcon className="w-4 h-4 text-danger-600 hover:text-danger-700" />
                      )}
                    </button>
                  </div>
                </div>
                <div className="p-1 ">
                  {editingId === index ? (
                    <div>
                      <input
                        type="text"
                        value={editTitle}
                        onChange={e => setEditTitle(e.target.value)}
                        className="border px-2 py-1 rounded text-sm w-full mb-3"
                        placeholder="Enter new title"
                      />
                      <div className="flex gap-2 justify-end mr-2">
                        <Button
                          variant="link"
                          size="inline"
                          onClick={cancelEditing}
                          className="text-neutral-600 hover:text-neutral-800"
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="link"
                          size="inline"
                          onClick={() => handleUpdateTitle(artistId, index, editTitle)}
                          disabled={updating}
                        >
                          Save
                        </Button>

                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between h-fit p-2">
                      <p className="font-medium text-sm truncate">{asset.imageTitle}</p>
                      <button
                        onClick={() => startEditing(index, asset)}
                        className="text-primary-700"
                        title="Edit Title"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-black">Artist Assets</h2>
              <p className="text-sm text-neutral-500 mt-1">
                Manage photos and other assets for this artist
              </p>
            </div>
          </div>
          <div className="text-center py-12">
            <div className="mx-auto w-24 h-24 bg-neutral-100 rounded-full flex items-center justify-center mb-4">
              <PhotoIcon className="w-12 h-12 text-neutral-400" />
            </div>
            <p className="text-neutral-500 mb-6">
              Upload photos and other assets for this artist.
            </p>
            <Button onClick={() => setIsModalOpen(true)} data-tour="upload-asset-btn">
              <ArrowUpTrayIcon className="w-4 h-4" />
              Upload Asset
            </Button>
          </div>
        </>
      )}

      <UploadModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        artistId={artistId}
        onUploadSuccess={handleUploadSuccess}
        onSelectionChange={() => { }}
        showCheckboxes={false}
        initialSelectedImages={[]}
      />
    </div>
  );
};

export default AssetsTab;
